import useApi from 'hooks/useApi';
import { requiredCheckbox, stringRequired } from 'libs/validations';
import React, { FunctionComponent, useEffect } from 'react';
import * as Yup from 'yup';
import { BankAccount, Confirmation, YourContact } from 'pages/Payments/PaymentMethods/Cards';
import PaymentMethodLayout from 'pages/Payments/PaymentMethods/Layout';
import useFetchResource from 'hooks/useFetchResource';
import { IInvoice } from './ChoosePaymentType';
import api from 'libs/api';
import useOnSubmit from 'hooks/useOnSubmit';
import OverlaySpinner from 'components/OverlaySpinner';
import { useParams } from 'react-router-dom';

interface IDebitFormValues {
  account_number: string;
  bank_name: string;
  company_name: string;
  is_agreement_payment: boolean;
  is_authorized_to_payments: boolean;
  institution_number: string;
  transit_number: string;
  user_name: string;
  user_position: string;
  user_email: string;
}

const values: IDebitFormValues = {
  account_number: '',
  bank_name: '',
  company_name: '',
  institution_number: '',
  is_agreement_payment: false,
  is_authorized_to_payments: false,
  transit_number: '',
  user_email: '',
  user_name: '',
  user_position: '',
};

const validationSchema = Yup.object().shape({
  account_number: stringRequired,
  bank_name: stringRequired.max(140, 'Must be 140 characters or fewer'),
  institution_number: stringRequired,
  is_agreement_payment: requiredCheckbox,
  is_authorized_to_payments: requiredCheckbox,
  transit_number: stringRequired,
  user_email: stringRequired,
  user_name: stringRequired,
  user_position: stringRequired,
});

const formCards = [
  {
    component: () => <BankAccount />,
    title: 'Bank Account Information',
  },
  {
    component: () => <YourContact />,
    title: 'Your contact information',
  },
  {
    component: () => <Confirmation />,
    title: 'Confirmation',
  },
];

const initialValues = {
  id: 0,
  guid: '',
  plan_name: '',
  total_price: 0,
  is_paid: false,
  is_processing: false,
  payment_types: {
    credit_card: false,
    debit_card: false,
  },
};

const DebitCardPaymentType: FunctionComponent = () => {
  const { guid } = useParams();
  const { getApiPath } = useApi();
  const { resource: invoice, isLoading } = useFetchResource<IInvoice>({
    api: api.account.billing.invoices.getOne,
    initialParams: {
      guid,
    },
    initialValues,
  });
  const submitHandler = useOnSubmit({
    api: getApiPath('billing.invoices.payment.debit', 'create'),
    deps: [guid],
    onSuccess: () => (window.location.href = '/dashboard'),
    params: { guid },
    serializer: (data: IDebitFormValues) => ({ resource: data }),
    successMessage: 'Payment successfully saved',
  });

  useEffect(() => {
    if (invoice?.is_paid || invoice?.is_processing) {
      window.location.href = `/invoices/${invoice.guid}/choose-payment-type`;
    }
  }, [invoice]);

  return (
    <>
      {isLoading ? (
        <OverlaySpinner visible={isLoading} />
      ) : (
        <PaymentMethodLayout<IDebitFormValues>
          plan={invoice.plan_name}
          price={invoice.total_price.toString()}
          invoiceID={invoice.id}
          initialValues={values}
          validationSchema={validationSchema}
          title="Pre Authorized Debits / Electronic Fund Transfers"
          formCards={formCards}
          onSubmit={submitHandler}
        />
      )}
    </>
  );
};

export default DebitCardPaymentType;
